import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Center, Loader, Stack, Text } from "@mantine/core";
import { legacyCasesService } from "../../../services/legacyCases.service";
import type { LegacyDocumentSummary } from "../../../types/legacyCases";
import LegacyPdfViewer from "./LegacyPdfViewer";

interface LegacyDocumentContentProps {
  document: LegacyDocumentSummary;
}

export default function LegacyDocumentContent({
  document,
}: LegacyDocumentContentProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const active = useRef(true);
  const requestId = useRef(0);
  const blobUrl = useRef<string>();
  const handleLoad = useCallback(async () => {
    requestId.current += 1;
    const currentRequest = requestId.current;
    try {
      const blob = await legacyCasesService.document(document.contentUrl);
      if (!active.current || currentRequest !== requestId.current) return;
      if (blobUrl.current) URL.revokeObjectURL(blobUrl.current);
      blobUrl.current = URL.createObjectURL(blob);
      setUrl(blobUrl.current);
    } catch (failure) {
      if (active.current && currentRequest === requestId.current)
        setError((failure as Error).message);
    }
  }, [document.contentUrl]);
  const handleRetry = useCallback(() => {
    if (blobUrl.current) URL.revokeObjectURL(blobUrl.current);
    setError(null);
    setUrl(null);
    void handleLoad();
  }, [handleLoad]);

  useEffect(() => {
    active.current = true;
    void handleLoad();
    return () => {
      active.current = false;
      requestId.current += 1;
      if (blobUrl.current) URL.revokeObjectURL(blobUrl.current);
    };
  }, [handleLoad]);

  if (error)
    return (
      <Alert color="red" title="Document unavailable" role="alert">
        <Stack align="flex-start">
          <Text size="sm">{error}</Text>
          <Button onClick={handleRetry}>Retry document</Button>
        </Stack>
      </Alert>
    );
  if (!url)
    return (
      <Center mih={300}>
        <Loader aria-label="Loading document" />
      </Center>
    );
  return (
    <LegacyPdfViewer
      key={url}
      url={url}
      label={document.originalFileName}
      onRetry={handleRetry}
    />
  );
}
